import type { ReactNode } from 'react';

export function Badge({ tone = 'muted', children }: {
  tone?: 'ok' | 'warn' | 'danger' | 'info' | 'muted';
  children: ReactNode;
}) {
  return <span className={`arc-badge arc-badge-${tone}`}>{children}</span>;
}

/**
 * Status is never signalled by colour alone -- the dot plus the text label
 * carry it, so it survives greyscale and colour-blindness (doc 07 section 5).
 */
export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, 'ok' | 'warn' | 'danger' | 'info' | 'muted'> = {
    active: 'ok', registered: 'ok', ready: 'ok',
    provisioning: 'info', pending: 'info', draining: 'warn',
    suspended: 'warn', disabled: 'muted', closed: 'muted',
    failed: 'danger', unregistered: 'danger',
  };
  return (
    <Badge tone={map[status] ?? 'muted'}>
      <span className="arc-dot" aria-hidden="true" />
      {status}
    </Badge>
  );
}

export function Empty({ icon = '∅', title, hint, action }: {
  icon?: string; title: string; hint?: string; action?: ReactNode;
}) {
  return (
    <div className="arc-empty">
      <div className="arc-empty-icon" aria-hidden="true">{icon}</div>
      <div style={{ fontWeight: 600, color: 'var(--arc-fg)', marginBottom: 4 }}>{title}</div>
      {hint && <div style={{ fontSize: 13, maxWidth: 420, margin: '0 auto 14px' }}>{hint}</div>}
      {action}
    </div>
  );
}

export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <tbody>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }).map((__, c) => (
            <td key={c}>
              <div className="arc-skeleton" style={{ height: 13, width: c === 0 ? '55%' : '75%' }} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

export function Stat({ label, value, sub, tone }: {
  label: string; value: ReactNode; sub?: ReactNode | undefined; tone?: 'ok' | 'warn' | 'danger' | undefined;
}) {
  const colour = tone ? `var(--arc-${tone})` : undefined;
  return (
    <div className="arc-card arc-stat">
      <div className="arc-stat-label">{label}</div>
      <div className="arc-stat-value" style={colour ? { color: colour } : undefined}>{value}</div>
      {sub !== undefined && <div className="arc-stat-sub">{sub}</div>}
    </div>
  );
}

export function ErrorBanner({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : 'Something went wrong';
  const requestId =
    typeof error === 'object' && error !== null && 'problem' in error
      ? (error as { problem?: { request_id?: string } }).problem?.request_id
      : undefined;
  return (
    <div className="arc-banner arc-banner-danger">
      <span aria-hidden="true">⚠</span>
      <div>
        <div style={{ fontWeight: 600 }}>{message}</div>
        {/* Quoting this to support resolves straight to a trace (doc 05 §3). */}
        {requestId && <div className="arc-mono" style={{ opacity: .8, marginTop: 2 }}>{requestId}</div>}
      </div>
    </div>
  );
}
